import {
    IPersonalizationRequestData,
    PersonalizationApiSdk
} from '../../api/PersonalizationApiSdk';
import { API_REQUEST_TIMEOUT } from '../constants/Vto';
import { Personalization } from '../domain/entities/Personalization';

interface IPersonalizedData {
    [key: string]: Personalization[];
}

export interface IPromiseStack {
    key: string;
    resolve(personalizedData: Personalization[]): void;
    reject(error?: Error): void;
}
export interface IApiQueue {
    fields: string[];
    perlgemId: string;
}

export class PersonalizationRepository {
    private personalizationApiSdk: PersonalizationApiSdk;
    private personalizedData: IPersonalizedData = {};
    private promiseStack: IPromiseStack[] = [];
    private apiQueue: { [key: string]: IApiQueue } = {};
    private timeout: number;

    constructor(personalizationApiSdk: PersonalizationApiSdk) {
        this.personalizationApiSdk = personalizationApiSdk;
    }

    public async createPersonalizationData(requestData: IPersonalizationRequestData) {
        const data = await this.personalizationApiSdk.createPersonalizationData(requestData);

        return Personalization.fromJson(data);
    }

    public async getPersonalizationDataByPerlgemUserId(fields: string[], perlgemId: string) {
        const key = [...fields].sort().toString().concat(perlgemId);

        if (this.personalizedData[key]) {
            return this.personalizedData[key];
        }

        if (!this.apiQueue[key]) {
            this.apiQueue[key] = { fields, perlgemId };

            // Prevent multi parallel duplicate API calls
            clearTimeout(this.timeout);
            this.timeout = window.setTimeout(this.getPersonalizedData, API_REQUEST_TIMEOUT);
        }

        return new Promise((resolve, reject) => {
            this.promiseStack.push({ key, resolve, reject });
        });
    }

    public async deletePersonalizationDataByPerlgemUserId(perlgemId: string) {
        const response = await this.personalizationApiSdk.deletePersonalizationDataByPerlgemUserId(
            perlgemId
        );

        return response;
    }

    private getPersonalizedData = async () => {
        const apiQueue = Object.entries(this.apiQueue);

        for (const [index, api] of apiQueue) {
            const { fields, perlgemId } = api;

            const data = await this.personalizationApiSdk.getPersonalizationDataByPerlgemUserId(
                fields,
                perlgemId
            );

            if (Array.isArray(data)) {
                this.personalizedData[index] = data.map((match) => Personalization.fromJson(match));
            }
        }

        this.resolvePromise();
    };

    private resolvePromise() {
        this.promiseStack.forEach((promise) => {
            const personalizedData = this.personalizedData[promise.key];
            personalizedData
                ? promise.resolve(personalizedData)
                : promise.reject(new Error('No personalized data'));
        });

        this.promiseStack = [];
        this.apiQueue = {};
    }
}
